import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import React, { useState, memo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Center,
  Text,
  Flex,
  Box,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import PropTypes from 'prop-types';
import { FaArrowDown, FaArrowUp } from 'react-icons/fa';

function CommonTable({
  columns,
  data = [],
  excludes = [],
  getfilter,
  isLoading = false,
  isLink = false,
  parentLink = '/',
  keyValue,
  rowclick = [],
  tableTitle = '',
  isMerchantList = false,
  isSorting = false,
  defaultFilter,
  dynamicWidth,
  emptyStateMessage = 'No Data Available',
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [hoveredHeader, setHoveredHeader] = useState(null);
  const [filter, setFilter] = useState(
    defaultFilter || {
      sortBy: '',
      sortKey: '',
    },
  );
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const handleRowClick = (row, event) => {
    const isActionElement =
      event.target.closest('.no-navigate') !== null ||
      rowclick.includes(
        event.target.closest('td')?.getAttribute('data-column-id'),
      );

    if (isLink && !isActionElement && row.original?.[keyValue]) {
      const returnUrl = `${location.pathname}${location.search}`;
      navigate(`${parentLink}/${row.original[keyValue]}`, {
        state: { returnUrl },
      });
    }
  };

  return (
    <Card {...(isMerchantList ? { minH: '600px', position: 'relative' } : {})}>
      {isLoading && (
        <>
          <Box
            position="absolute"
            top="0"
            left="0"
            right="0"
            bottom="0"
            backgroundColor="rgba(85, 85, 85, 0.1)"
            zIndex="9"
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <Flex direction="column" align="center" justify="center">
              <Spinner size="xl" color="black" thickness="4px" />
            </Flex>
          </Box>
        </>
      )}
      <Flex
        flexDirection="column"
        w="100%"
        overflowX="auto"
        position={'relative'}
        minHeight={data?.length > 10 ? '62vh' : ''}
      >
        <Flex px="25px" mb="8px" justifyContent="space-between" align="center">
          <Text
            fontSize="22px"
            fontWeight="700"
            lineHeight="100%"
            color="secondaryGray.900"
          >
            {tableTitle}
          </Text>
        </Flex>
        <Table
          variant="simple"
          minWidth={'600px'}
          sx={{ tableLayout: 'fixed' }}
        >
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th
                    userSelect="none"
                    borderColor="#e9ecef"
                    fontFamily="Montserrat"
                    key={header.id}
                    // width={'200px'}
                    width={
                      dynamicWidth && dynamicWidth[header.id]
                        ? dynamicWidth[header.id]
                        : '200px'
                    }
                    className={
                      rowclick.includes(header.id) ? 'no-navigate' : ''
                    }
                    onMouseEnter={() => {
                      if (excludes.includes(header.id)) {
                        setHoveredHeader(header.id);
                      }
                    }}
                    onMouseLeave={() => setHoveredHeader(null)}
                    onClick={() => {
                      if (excludes.includes(header.id)) {
                        // Calculate new filter values first
                        const isSameColumn = filter.sortKey === header.id;
                        const newSortKey = isSameColumn
                          ? filter.sortKey
                          : header.id;
                        const newSortBy = isSameColumn
                          ? filter.sortBy === 1
                            ? -1
                            : 1
                          : 1;

                        // Update local filter state
                        setFilter({
                          sortKey: newSortKey,
                          sortBy: newSortBy,
                        });

                        // Pass the new filter values to parent
                        if (typeof getfilter === 'function') {
                          getfilter({
                            sortKey: newSortKey,
                            sortBy: newSortBy,
                          });
                        }
                      }
                    }}
                    position="relative"
                    cursor={
                      excludes.includes(header.id) && isSorting
                        ? 'pointer'
                        : 'default'
                    }
                    _hover={{
                      bg: excludes.includes(header.id) ? 'gray.200' : 'inherit',
                    }}
                  >
                    <Flex align="center">
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                      {(hoveredHeader === header.id && isSorting) ||
                      filter.sortKey === header.id ? (
                        <Box position="absolute" ml="-4">
                          {filter.sortKey === header.id ? (
                            filter.sortBy === 1 ? (
                              <FaArrowUp color="#00cc66" fontSize="15px" />
                            ) : (
                              <FaArrowDown color="#00cc66" fontSize="15px" />
                            )
                          ) : (
                            <FaArrowUp color="gray.300" fontSize="15px" />
                          )}
                        </Box>
                      ) : null}
                    </Flex>
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {data?.length > 0 ? (
              table.getRowModel().rows.map((row) => (
                <Tr
                  key={row.id}
                  cursor={
                    isLink && row.original?.[keyValue] ? 'pointer' : 'default'
                  }
                  _hover={{
                    bg:
                      isLink && row.original?.[keyValue]
                        ? 'gray.100'
                        : 'transparent',
                  }}
                  onClick={(e) => handleRowClick(row, e)}
                  className={isLink ? 'cursor-pointer hover:bg-gray-50' : ''}
                >
                  {row.getVisibleCells().map((cell) => (
                    <Td
                      key={cell.id}
                      data-column-id={cell.column.id}
                      // width={'200px'}
                      maxWidth={
                        dynamicWidth && dynamicWidth[cell.column.id]
                          ? dynamicWidth[cell.column.id]
                          : '200px'
                      }
                      // width={dynamicWidth.includes(cell.column.id)? "cc" : ""}
                      className={
                        rowclick.includes(cell.column.id) ? 'no-navigate' : ''
                      }
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </Td>
                  ))}
                </Tr>
              ))
            ) : (
              <>
                <Tr>
                  <Td colSpan={columns.length}>
                    <Center>
                      <Text>{emptyStateMessage}</Text>
                    </Center>
                  </Td>
                </Tr>
              </>
            )}
          </Tbody>
        </Table>
      </Flex>
    </Card>
  );
}

CommonTable.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array,
  excludes: PropTypes.array,
  getfilter: PropTypes.func,
  isLoading: PropTypes.bool.isRequired,
  isLink: PropTypes.bool,
  parentLink: PropTypes.string,
  keyValue: PropTypes.string.isRequired,
  rowclick: PropTypes.array,
  tableTitle: PropTypes.string,
  isMerchantList: PropTypes.bool,
  isSorting: PropTypes.bool,
  defaultFilter: PropTypes.object,
  dynamicWidth: PropTypes.object,
  emptyStateMessage: PropTypes.string,
};

export default memo(CommonTable);
